import React, { useState, useEffect, useRef, useMemo } from "react";
import { Check, Search, X, ChevronDown } from "lucide-react";
import { Popover } from "./Popover";

export interface ComboboxItem {
  id: string;
  label: string;
  count?: number;
}

interface ComboboxProps {
  items: ComboboxItem[];
  selectedIds: Set<string>;
  onSelect: (id: string) => void;
  onClearAll?: () => void;
  placeholder?: string;
  searchPlaceholder?: string;
  loading?: boolean;
  error?: boolean;
  onRetry?: () => void;
}

export function Combobox({
  items = [],
  selectedIds,
  onSelect,
  onClearAll,
  placeholder = "Select...",
  searchPlaceholder = "Search...",
  loading = false,
  error = false,
  onRetry,
}: ComboboxProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const safeItems = Array.isArray(items) ? items : [];

  // Prefix match first, then contains.
  const filteredItems = useMemo(() => {
    if (!query.trim()) return safeItems.slice(0, 50);
    const q = query.toLowerCase();
    const prefix: ComboboxItem[] = [];
    const contains: ComboboxItem[] = [];
    
    for (const item of safeItems) {
      if (!item || !item.label) continue;
      const lbl = item.label.toLowerCase();
      if (lbl.startsWith(q)) {
        prefix.push(item);
      } else if (lbl.includes(q)) {
        contains.push(item);
      }
      if (prefix.length + contains.length >= 20) break;
    }
    return [...prefix, ...contains].slice(0, 20);
  }, [items, query]);

  useEffect(() => {
    if (open) {
      setQuery("");
      setFocusedIndex(-1);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  useEffect(() => {
    setFocusedIndex(-1);
  }, [query]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setFocusedIndex(i => Math.min(i + 1, filteredItems.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setFocusedIndex(i => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (focusedIndex >= 0 && focusedIndex < filteredItems.length) {
        onSelect(filteredItems[focusedIndex].id);
      } else if (filteredItems.length === 1) {
        onSelect(filteredItems[0].id);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  useEffect(() => {
    if (focusedIndex >= 0 && listRef.current) {
      const el = listRef.current.children[focusedIndex] as HTMLElement;
      if (el && el.scrollIntoView) {
        el.scrollIntoView({ block: "nearest" });
      }
    }
  }, [focusedIndex]);

  const selectedCount = selectedIds.size;
  const triggerLabel = selectedCount === 0 
    ? placeholder 
    : selectedCount === 1 
      ? safeItems.find(i => selectedIds.has(i.id))?.label || `${selectedCount} selected` 
      : `${selectedCount} selected`;

  const trigger = (
    <button 
      type="button"
      className="flex items-center justify-between w-full h-12 px-3 text-sm bg-transparent border border-slate-700/50 rounded-lg hover:border-slate-500 transition-colors group ui-open:border-slate-500"
    >
      <span className="truncate text-white">{triggerLabel}</span>
      <ChevronDown size={16} className="text-slate-500 group-hover:text-slate-400 transition-transform ui-open:rotate-180 flex-shrink-0" />
    </button>
  );

  return (
    <Popover trigger={trigger} open={open} onOpenChange={setOpen} className="w-full">
      <div className="p-2 border-b border-slate-700/50 flex items-center">
        <Search size={16} className="text-slate-500 mr-2 flex-shrink-0" />
        <input
          ref={inputRef}
          type="text"
          className="w-full bg-transparent outline-none text-sm text-white placeholder-slate-500"
          placeholder={searchPlaceholder}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
        />
        {onClearAll && selectedCount > 0 && (
          <button 
            type="button"
            onClick={onClearAll}
            className="text-xs text-indigo-400 hover:text-indigo-300 whitespace-nowrap ml-2"
          >
            Clear all
          </button>
        )}
      </div>
      <ul
        ref={listRef}
        className="max-h-64 overflow-y-auto py-1 outline-none"
        role="listbox"
      >
        {loading ? (
          <li className="px-3 py-4 text-center text-sm text-slate-400">Loading...</li>
        ) : error ? (
          <li className="px-3 py-4 text-center text-sm text-red-400 flex flex-col items-center gap-2">
            <span>Couldn't load options</span>
            {onRetry && (
              <button 
                onClick={(e) => { e.stopPropagation(); onRetry(); }}
                className="text-xs bg-slate-800 hover:bg-slate-700 px-3 py-1 rounded text-white"
              >
                Retry
              </button>
            )}
          </li>
        ) : filteredItems.length === 0 ? (
          <li className="px-3 py-4 text-center text-sm text-slate-400">No matches</li>
        ) : (
          filteredItems.map((item, index) => {
            const isSelected = selectedIds.has(item.id);
            return (
              <li
                key={item.id}
                role="option"
                aria-selected={isSelected}
                className={`flex items-center justify-between px-3 py-2 text-sm cursor-pointer transition-colors ${
                  focusedIndex === index ? "bg-slate-800 text-white" : "text-slate-300 hover:bg-slate-800/50"
                }`}
                onClick={() => onSelect(item.id)}
                onMouseEnter={() => setFocusedIndex(index)}
              >
                <div className="flex items-center gap-2 overflow-hidden">
                  <div className={`w-4 h-4 flex-shrink-0 flex items-center justify-center border rounded ${isSelected ? "border-indigo-500 bg-indigo-500 text-white" : "border-slate-600 bg-transparent"}`}>
                    {isSelected && <Check size={12} strokeWidth={3} />}
                  </div>
                  <span className="truncate">{item.label}</span>
                </div>
                {item.count !== undefined && (
                  <span className="text-xs text-slate-500 flex-shrink-0 ml-2">{item.count}</span>
                )}
              </li>
            );
          })
        )}
      </ul>
      <div aria-live="polite" className="sr-only">
        {filteredItems.length} suggestions found.
      </div>
    </Popover>
  );
}
